/**
 * Native API origin shim — bundled Capacitor builds only.
 *
 * On web the page origin IS the API origin, so `fetch('/api/...')` works. In a
 * BUNDLED native build (CAP_ENV unset) the WebView origin is `https://localhost`
 * (Android) / `capacitor://localhost` (iOS) while the API lives at
 * `VITE_API_URL`, so those 14 relative call sites resolve to Capacitor's local
 * asset server instead. That server answers unknown paths with the SPA fallback,
 * so the calls do not fail loudly — they return `200 text/html` containing
 * `index.html`, which callers then try to parse as JSON. Verified on a bundled
 * Android build: `fetch('/api/auth/me')` → 200 `<!DOCTYPE html>`.
 *
 * This rewrites same-origin `/api/*` requests onto `API_BASE_URL` so relative
 * and absolute call sites behave identically. Both transports are covered:
 * `fetch` (14 call sites) and `XMLHttpRequest` (3 raw `axios.get('/api/...')`
 * calls, which use the default axios instance and so have no `baseURL`).
 * See Card A's decision record in `src/docs/GOOGLE_OAUTH_NATIVE_MOBILE_PLAN.md` §9.
 *
 * No-op on web, and no-op in the `CAP_ENV=beta` remote-URL wrapper (there the
 * WebView already loads from the API's own origin, so nothing needs rewriting).
 */

import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { API_BASE_URL } from './api';
import { getSessionId, captureSessionId } from '@/services/nativeSession';

function rewriteIfRelativeApiCall(rawUrl: string, apiOrigin: string): string {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl, window.location.href);
  } catch {
    return rawUrl;
  }

  // Only same-origin /api/* requests are ambiguous; everything else (bundled
  // assets, already-absolute API calls, Supabase, Stripe, R2) is left alone.
  if (parsed.origin !== window.location.origin) return rawUrl;
  if (!parsed.pathname.startsWith('/api/')) return rawUrl;

  return `${apiOrigin.replace(/\/$/, '')}${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/** Is this a request to our own API (and so eligible for the session header)? */
function isApiRequest(rawUrl: string, apiOrigin: string): boolean {
  try {
    const parsed = new URL(rawUrl, window.location.href);
    return parsed.origin === apiOrigin && parsed.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

export function installNativeApiOriginShim(): void {
  if (!Capacitor.isNativePlatform()) return;
  if (!API_BASE_URL) return;

  let apiOrigin: string;
  try {
    apiOrigin = new URL(API_BASE_URL, window.location.href).origin;
  } catch {
    return;
  }

  // Origin rewriting is a BUNDLED-only concern: in the CAP_ENV=beta wrapper the
  // page already loads from the API's origin. The session header below is NOT —
  // native authenticates by bearer in both flavors, so there is one native path.
  const needsOriginRewrite = apiOrigin !== window.location.origin;

  /**
   * Attach the native session credential. `credentials: 'include'` stays on the
   * requests, harmlessly — on native the cookie is simply never the thing that
   * authenticates (see services/nativeSession.ts for why it cannot be).
   */
  function withSessionHeaders(url: string, init?: RequestInit): RequestInit | undefined {
    if (!isApiRequest(url, apiOrigin)) return init;

    const headers = new Headers(init?.headers);
    // Tells the backend to hand back X-Session-Id on session-issuing routes.
    headers.set('X-Client-Platform', 'native');
    const sessionId = getSessionId();
    if (sessionId && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${sessionId}`);
    }
    return { ...init, headers };
  }

  const originalFetch = window.fetch;
  window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
    let url: string;
    let request: RequestInfo | URL = input;

    if (typeof input === 'string' || input instanceof URL) {
      url = needsOriginRewrite
        ? rewriteIfRelativeApiCall(String(input), API_BASE_URL)
        : String(input);
      request = url;
    } else {
      url = needsOriginRewrite ? rewriteIfRelativeApiCall(input.url, API_BASE_URL) : input.url;
      request = url === input.url ? input : new Request(url, input);
    }

    // A Request object carries its own headers, so merge ours onto a rebuilt one.
    if (request instanceof Request) {
      const merged = withSessionHeaders(url, { headers: request.headers });
      request = merged ? new Request(request, { headers: merged.headers }) : request;
      const response = await originalFetch(request);
      await captureSessionId(response);
      return response;
    }

    const response = await originalFetch(request, withSessionHeaders(url, init));
    // Login / signup / OAuth exchange all funnel through here, so this is the
    // single place a newly issued session id can be picked up.
    await captureSessionId(response);
    return response;
  };

  // The default axios instance has no `baseURL`, so `axios.get('/api/...')`
  // (AppLayout's unread-count badges, push registration) goes out over XHR
  // against the page origin. Setting the default instance's baseURL is what
  // reaches those call sites; absolute URLs are unaffected, since axios ignores
  // baseURL when the URL is already absolute.
  if (needsOriginRewrite) {
    axios.defaults.baseURL = API_BASE_URL;
  }

  // XHR bypasses the fetch patch entirely, so the default instance needs its own
  // interceptor for the session header.
  axios.interceptors.request.use((config) => {
    const url = new URL(config.url || '', config.baseURL || window.location.href).toString();
    if (!isApiRequest(url, apiOrigin)) return config;
    config.headers.set('X-Client-Platform', 'native');
    const sessionId = getSessionId();
    if (sessionId) config.headers.set('Authorization', `Bearer ${sessionId}`);
    return config;
  });

  console.log(
    `[NativeApiOrigin] Native transport ready (bearer session; rewrite=${needsOriginRewrite})`
  );
}
